import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import wsService from '@/services/websocket'
import type { Message, Source } from '@/types'

export interface ChatHistoryItem {
  id: string
  title: string
  timestamp: Date
  messages: Message[]
  pinned?: boolean
  favorite?: boolean
}

function getUserId(): string | null {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null')
    return user?.user_id ? String(user.user_id) : null
  } catch {
    return null
  }
}

function historyKey(): string {
  const uid = getUserId()
  return uid ? `chatHistory_${uid}` : 'chatHistory'
}

function favoritesKey(): string {
  const uid = getUserId()
  return uid ? `chatFavorites_${uid}` : 'chatFavorites'
}

function unreadKey(): string {
  const uid = getUserId()
  return uid ? `mis_unread_chats_${uid}` : 'mis_unread_chats'
}

export const useChatStore = defineStore('chat', () => {
  const messages = ref<Message[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isStreaming = ref(false)
  const useWebSocket = ref(false)
  const isHistoryLoading = ref(false)  // Loading state for history
  const chatMode = ref<'assistant' | 'escalated'>('assistant')
  let pendingReplyTimer: ReturnType<typeof setInterval> | null = null
  let streamResolve: (() => void) | null = null
  let streamReject: ((err: Error) => void) | null = null
  let streamTimer: ReturnType<typeof setTimeout> | null = null
  
  // History and favorites
  const chatHistory = ref<ChatHistoryItem[]>([])
  const favorites = ref<Message[]>([])
  const currentChatId = ref<string | null>(null)

  // Непрочитанные чаты (точки в истории, как в мессенджерах).
  // Источник — ответ администратора, пришедший по поллингу, пока
  // вкладка была скрыта. Снимается открытием чата.
  const unreadChatIds = ref<string[]>([])
  try {
    unreadChatIds.value = JSON.parse(localStorage.getItem(unreadKey()) || '[]')
  } catch {
    unreadChatIds.value = []
  }
  const persistUnread = () => {
    try {
      localStorage.setItem(unreadKey(), JSON.stringify(unreadChatIds.value))
    } catch { /* non-critical */ }
  }
  const markChatUnread = (chatId: string | null) => {
    if (!chatId || unreadChatIds.value.includes(chatId)) return
    unreadChatIds.value.push(chatId)
    persistUnread()
  }
  const markChatRead = (chatId: string | null) => {
    if (!chatId) return
    unreadChatIds.value = unreadChatIds.value.filter(id => id !== chatId)
    persistUnread()
  }

  const generateId = () => `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`

  const hasMessages = computed(() => messages.value.length > 0)
  const lastMessage = computed(() => messages.value[messages.value.length - 1])

  const addMessage = (
    content: string,
    role: Message['role'],
    sources?: Source[]
  ) => {
    const message: Message = {
      id: generateId(),
      role,
      content,
      timestamp: new Date(),
      sources,
      _toggleSources: false,
    }
    messages.value.push(message)
    return message
  }

  const updateLastMessage = (content: string) => {
    const lastMsg = messages.value[messages.value.length - 1]
    if (lastMsg) {
      lastMsg.content += content
    }
  }

  // WebSocket connection for streaming
  const connectWebSocket = async () => {
    try {
      await wsService.connect()
      useWebSocket.value = true
      
      wsService.onMessage((data) => {
        if (typeof data === 'object' && data !== null) {
          const msg = data as Record<string, unknown>
          
          if (msg.type === 'token') {
            updateLastMessage(msg.content as string)
          } else if (msg.type === 'done') {
            isStreaming.value = false
            streamResolve?.()
            streamResolve = null
            if (streamTimer) { clearTimeout(streamTimer); streamTimer = null }
          } else if (msg.type === 'error') {
            error.value = msg.message as string
            isStreaming.value = false
            streamReject?.(new Error(msg.message as string))
            streamReject = null
            if (streamTimer) { clearTimeout(streamTimer); streamTimer = null }
          } else if (msg.type === 'message') {
            // Backend sends full message at once (non-streaming mode)
            const loadingIndex = messages.value.findIndex(m => m.isLoading)
            if (loadingIndex !== -1) {
              messages.value.splice(loadingIndex, 1)
            }
            addMessage(msg.content as string, 'assistant', msg.sources as Source[] | undefined)
            if ((msg as any).chat_mode === 'escalated') {
              startPollingForAdminReply()
            }
            isStreaming.value = false
            streamResolve?.()
            streamResolve = null
            if (streamTimer) { clearTimeout(streamTimer); streamTimer = null }
          } else if (msg.type === 'typing') {
            // Typing indicator, just keep loading state
          }
        }
      })
      
      wsService.onDisconnect(() => {
        useWebSocket.value = false
        isStreaming.value = false  // Разблокировать sendMessage при разрыве WebSocket
      })
    } catch (e) {
      console.warn('WebSocket not available, falling back to HTTP')
      useWebSocket.value = false
    }
  }

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading.value) return

    // Auto-save previous chat if starting a new one without an ID
    if (messages.value.length > 0 && !currentChatId.value) {
      saveToHistory()
    }
    if (!currentChatId.value) {
      currentChatId.value = generateId()
    }

    // Add user message
    addMessage(content, 'user')
    
    isLoading.value = true
    error.value = null

    // Add loading placeholder for assistant
    const loadingMessage: Message = {
      id: generateId(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true,
    }
    messages.value.push(loadingMessage)

    try {
      // Try WebSocket first for streaming
      if (useWebSocket.value && wsService.isConnected) {
        isStreaming.value = true
        wsService.send({ type: 'chat', message: content })
        
        // Wait for response via explicit callback (no race condition)
        await new Promise<void>((resolve, reject) => {
          streamResolve = resolve
          streamReject = reject
          streamTimer = setTimeout(() => {
            streamResolve = null
            streamReject = null
            isStreaming.value = false
            reject(new Error('Stream timeout'))
          }, 60000)
        })
      } else {
        // Fallback to HTTP
        const response = await api.askQuestion(content)
        
        // Remove loading message
        const loadingIndex = messages.value.findIndex(m => m.isLoading)
        if (loadingIndex !== -1) {
          messages.value.splice(loadingIndex, 1)
        }

        if (response.error) {
          addMessage(`Ошибка: ${response.error}`, 'assistant')
        } else {
          addMessage(response.answer, 'assistant', response.sources)
          if (response.chat_mode === 'escalated') {
            startPollingForAdminReply()
          }
        }
      }
    } catch (e) {
      // Remove loading message
      const loadingIndex = messages.value.findIndex(m => m.isLoading)
      if (loadingIndex !== -1) {
        messages.value.splice(loadingIndex, 1)
      }
      
      const errorMessage = e instanceof Error ? e.message : 'Ошибка соединения'
      addMessage(errorMessage, 'assistant')
      error.value = errorMessage
    } finally {
      streamResolve = null
      streamReject = null
      if (streamTimer) { clearTimeout(streamTimer); streamTimer = null }
      // Persist current conversation to history sidebar
      if (currentChatId.value && messages.value.length > 0) {
        saveToHistory()
      }
      isLoading.value = false
      isStreaming.value = false
    }
  }

  const cancelMessage = async () => {
    try {
      const data = await api.post<{ response_type?: string; message?: string }>('/chat/cancel')
      
      if (data.response_type === 'cancelled') {
        // Remove loading message
        const loadingIndex = messages.value.findIndex(m => m.isLoading)
        if (loadingIndex !== -1) {
          messages.value.splice(loadingIndex, 1)
        }
        addMessage(data.message || '✅ Генерация отменена.', 'assistant')
      } else {
        addMessage(data.message || 'Не удалось отменить генерацию.', 'assistant')
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Ошибка отмены'
      addMessage(errorMessage, 'assistant')
    } finally {
      isLoading.value = false
      isStreaming.value = false
    }
  }

  const clearChat = async () => {
    // Save current chat to history if it has messages
    if (messages.value.length > 0) {
      saveToHistory()
    }
    
    messages.value = []
    currentChatId.value = null
    
    try {
      // baseURL уже '/api', поэтому endpoint без префикса
      await api.post('/chat/clear')
    } catch {
      // Ignore clear errors
    }
  }

  const saveToHistory = () => {
    if (messages.value.length === 0) return
    
    const firstMessage = messages.value[0]
    const title = firstMessage.content.substring(0, 50) + (firstMessage.content.length > 50 ? '...' : '')
    
    const historyItem: ChatHistoryItem = {
      id: currentChatId.value || generateId(),
      title,
      timestamp: new Date(),
      messages: [...messages.value],
    }
    
    // Deduplicate: update existing entry or insert new one
    const existingIndex = chatHistory.value.findIndex(c => c.id === historyItem.id)
    if (existingIndex !== -1) {
      chatHistory.value[existingIndex] = historyItem
    } else {
      chatHistory.value.unshift(historyItem)
    }
    
    // Sort by timestamp descending (pinned first)
    chatHistory.value.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1
      if (!a.pinned && b.pinned) return 1
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    })
    
    // Keep only last 20 chats
    if (chatHistory.value.length > 20) {
      chatHistory.value = chatHistory.value.slice(0, 20)
    }
    
    // Save to localStorage
    localStorage.setItem(historyKey(), JSON.stringify(chatHistory.value))
    
    // Save to backend (fire-and-forget)
    const chatForApi = {
      ...historyItem,
      timestamp: historyItem.timestamp.toISOString(),
      messages: historyItem.messages.map(m => ({
        ...m,
        timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
      })),
    }
    api.saveChatHistory(chatForApi).catch(() => {})
  }

  const loadHistory = async () => {
    isHistoryLoading.value = true
    
    try {
      // Try to load from backend API first
      try {
        const data = await api.getChatHistory()
        if (data.history && Array.isArray(data.history) && data.history.length > 0) {
          chatHistory.value = data.history.map((chat: any) => ({
            id: chat.id || `chat-${Date.now()}`,
            title: chat.title || (chat.messages?.[0]?.content?.substring(0, 30)) || 'Без названия',
            timestamp: new Date(chat.timestamp),
            messages: (chat.messages || []).map((m: any) => ({
              ...m,
              timestamp: new Date(m.timestamp),
            })),
            pinned: chat.pinned || false,
          }))
          localStorage.setItem(historyKey(), JSON.stringify(chatHistory.value))
        }
      } catch {
        console.log('API not available, loading from localStorage')
      }
      
      // Load from localStorage anyway
      const saved = localStorage.getItem(historyKey())
      if (saved) {
        try {
          const parsed = JSON.parse(saved)
          // Merge with API data or use local only
          if (chatHistory.value.length === 0 && parsed.length > 0) {
            chatHistory.value = parsed.map((chat: any) => ({
              ...chat,
              timestamp: new Date(chat.timestamp),
              messages: (chat.messages || []).map((m: any) => ({
                ...m,
                timestamp: new Date(m.timestamp),
              })),
            }))
          }
        } catch {
          // Ignore parse errors
        }
      }
      
      // Load favorites
      const savedFavorites = localStorage.getItem(favoritesKey())
      if (savedFavorites) {
        favorites.value = JSON.parse(savedFavorites)
      }
    } finally {
      isHistoryLoading.value = false
    }
  }

  const loadChat = (chatId: string) => {
    const chat = chatHistory.value.find(c => c.id === chatId)
    if (chat) {
      messages.value = [...chat.messages]
      currentChatId.value = chatId
      markChatRead(chatId)
    }
  }

  const addToFavorites = (message: Message) => {
    if (!favorites.value.find(f => f.id === message.id)) {
      favorites.value.push(message)
      localStorage.setItem(favoritesKey(), JSON.stringify(favorites.value))
    }
  }

  const removeFromFavorites = (messageId: string) => {
    favorites.value = favorites.value.filter(f => f.id !== messageId)
    localStorage.setItem(favoritesKey(), JSON.stringify(favorites.value))
  }

  const isFavorite = (messageId: string) => {
    return favorites.value.some(f => f.id === messageId)
  }

  const deleteFromHistory = (chatId: string) => {
    chatHistory.value = chatHistory.value.filter(c => c.id !== chatId)
    markChatRead(chatId)
    localStorage.setItem(historyKey(), JSON.stringify(chatHistory.value))
    api.deleteChatHistory(chatId).catch(() => {})
  }

  const createNewChat = () => {
    if (messages.value.length > 0) {
      saveToHistory()
    }
    currentChatId.value = null
    messages.value = []
  }

  const toggleChatFavorite = (chatId: string) => {
    const chat = chatHistory.value.find(c => c.id === chatId)
    if (chat) {
      chat.favorite = !chat.favorite
      chatHistory.value.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1
        if (!a.pinned && b.pinned) return 1
        if (a.favorite && !b.favorite) return -1
        if (!a.favorite && b.favorite) return 1
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      })
      localStorage.setItem(historyKey(), JSON.stringify(chatHistory.value))
      const chatForApi = {
        ...chat,
        timestamp: chat.timestamp instanceof Date ? chat.timestamp.toISOString() : chat.timestamp,
        messages: chat.messages.map(m => ({
          ...m,
          timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
        })),
      }
      api.saveChatHistory(chatForApi).catch(() => {})
    }
  }

  const pinChat = (chatId: string) => {
    const chat = chatHistory.value.find(c => c.id === chatId)
    if (chat) {
      chat.pinned = !chat.pinned
      // Move pinned chats to top
      chatHistory.value.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1
        if (!a.pinned && b.pinned) return 1
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      })
      localStorage.setItem(historyKey(), JSON.stringify(chatHistory.value))
      const chatForApi = {
        ...chat,
        timestamp: chat.timestamp instanceof Date ? chat.timestamp.toISOString() : chat.timestamp,
        messages: chat.messages.map(m => ({
          ...m,
          timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
        })),
      }
      api.saveChatHistory(chatForApi).catch(() => {})
    }
  }

  // Lazy initializer — call from page onMounted
  let initialized = false
  const startPollingForAdminReply = () => {
    stopPollingForAdminReply()
    chatMode.value = 'escalated'
    pendingReplyTimer = setInterval(async () => {
      try {
        const data = await api.getPendingReply()
        if (data.reply) {
          const replyMsg: Message = {
            id: generateId(),
            role: 'system',
            content: `✉️ Ответ от ${data.reply.admin_name}:\n\n${data.reply.text}`,
            timestamp: new Date(data.reply.timestamp || Date.now()),
          }
          messages.value.push(replyMsg)
          saveToHistory()
          // Вкладка скрыта — пользователь ответ не видел: точка в истории.
          if (typeof document !== 'undefined' && document.hidden) {
            markChatUnread(currentChatId.value)
          }
        }
      } catch {
        // polling errors are non-critical
      }
    }, 15000)
  }

  const stopPollingForAdminReply = () => {
    if (pendingReplyTimer) {
      clearInterval(pendingReplyTimer)
      pendingReplyTimer = null
    }
    chatMode.value = 'assistant'
  }

  const checkActiveEscalation = async () => {
    try {
      const data = await api.getPendingReply()
      if (data.active) {
        startPollingForAdminReply()
      } else if (chatMode.value === 'escalated') {
        stopPollingForAdminReply()
      }
    } catch {
      // non-critical
    }
  }

  // Demo mode
  const demoActive = ref(false)

  const startDemo = () => {
    demoActive.value = true
  }

  const stopDemo = () => {
    demoActive.value = false
  }

  const init = async () => {
    if (initialized) return
    initialized = true
    await loadHistory()
    connectWebSocket()
    checkActiveEscalation()
  }

  return {
    messages,
    isLoading,
    error,
    isStreaming,
    useWebSocket,
    chatHistory,
    favorites,
    currentChatId,
    unreadChatIds,
    markChatRead,
    markChatUnread,
    hasMessages,
    lastMessage,
    addMessage,
    sendMessage,
    cancelMessage,
    clearChat,
    saveToHistory,
    loadHistory,
    loadChat,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    deleteFromHistory,
    createNewChat,
    pinChat,
    toggleChatFavorite,
    connectWebSocket,
    isHistoryLoading,
    init,
    chatMode,
    startPollingForAdminReply,
    stopPollingForAdminReply,
    demoActive,
    startDemo,
    stopDemo,
  }
})
